import { RecentlyViewed } from '@theme/recently-viewed-products';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedProductsBlock extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #contentObserver = null;

  #arrowFrame = 0;

  connectedCallback() {
    this.#resizeObserver = new ResizeObserver(this.#scheduleArrowPosition);
    this.#contentObserver = new MutationObserver(() => this.#observeMedia());
    this.#contentObserver.observe(this, { childList: true, subtree: true });
    this.addEventListener('load', this.#scheduleArrowPosition, true);
    this.addEventListener('scroll', this.#scheduleArrowPosition, true);
    this.#observeMedia();

    if (this.dataset.productsLoaded === 'true') return;

    const currentProductId = this.dataset.currentProductId;
    const productIds = RecentlyViewed.getProducts()
      .filter((productId) => productId && productId !== currentProductId)
      .slice(0, 10);

    if (productIds.length === 0) {
      this.#showEmptyState();
      return;
    }

    this.dataset.hasHistory = 'true';
    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;

        observer.disconnect();
        this.#loadProducts(productIds);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#contentObserver?.disconnect();
    this.removeEventListener('load', this.#scheduleArrowPosition, true);
    this.removeEventListener('scroll', this.#scheduleArrowPosition, true);
    cancelAnimationFrame(this.#arrowFrame);
    this.#arrowFrame = 0;
  }

  // Hydration can replace the cards without reconnecting this block.
  #observeMedia() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver?.observe(this);
    this.querySelectorAll('.resource-list__carousel .card-gallery').forEach((media) => {
      this.#resizeObserver?.observe(media);
    });
    this.#scheduleArrowPosition();
  }

  #scheduleArrowPosition = () => {
    if (this.#arrowFrame) return;
    this.#arrowFrame = requestAnimationFrame(() => {
      this.#arrowFrame = 0;
      if (!this.isConnected) return;

      this.querySelectorAll('.resource-list__carousel').forEach((carousel) => {
        const container = carousel.querySelector(':scope > slideshow-container');
        if (!(container instanceof HTMLElement)) return;
        const containerRect = container.getBoundingClientRect();
        if (!containerRect.width) return;

        // Use a visible card's actual media, so titles, prices and hover padding
        // never change the arrow's vertical center, including after images load.
        const galleries = carousel.querySelectorAll('.card-gallery');
        for (const gallery of galleries) {
          const media = gallery.querySelector('slideshow-container') || gallery;
          const mediaRect = media.getBoundingClientRect();
          const visibleWidth = Math.min(mediaRect.right, containerRect.right) - Math.max(mediaRect.left, containerRect.left);
          if (!mediaRect.height || visibleWidth < Math.min(mediaRect.width / 2, containerRect.width / 2)) continue;

          const center = mediaRect.top - containerRect.top + mediaRect.height / 2;
          carousel.style.setProperty('--recently-viewed-arrow-top', `${center}px`);
          break;
        }
      });
    });
  };

  #showEmptyState() {
    this.dataset.hasHistory = 'true';
    this.dataset.productsLoaded = 'true';
    this.setAttribute('aria-busy', 'false');
    this.querySelector('[data-recently-viewed-loading]')?.setAttribute('hidden', '');
    this.querySelector('[data-recently-viewed-empty]')?.removeAttribute('hidden');
  }

  /**
   * Fetches this block's parent section against a product-ID search and hydrates only keyed content.
   * @param {string[]} productIds
   */
  async #loadProducts(productIds) {
    const { searchUrl, sectionId } = this.dataset;
    if (!searchUrl || !sectionId) return;

    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', productIds.map((productId) => `id:${productId}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url, abortController.signal);
      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      if (abortController.signal.aborted) return;
      if (!Shopify.designMode) console.error('Recently viewed products error:', error);
      this.hidden = true;
    } finally {
      if (this.#abortController === abortController) this.#abortController = null;
    }
  }
}

if (!customElements.get('recently-viewed-products-block')) {
  customElements.define('recently-viewed-products-block', RecentlyViewedProductsBlock);
}
